<template>
  <div>
    <b-row>
      <b-col lg="3">
        <b-form-group
          label="Group by:"
          label-for="group-by"
          label-cols-sm="4"
          label-align-sm="left"
          label-size="sm"
          class="mb-0"
        >
          <b-form-select
            id="group-by"
            v-model="groupByLocal"
            size="sm"
            :options="groupByOptions"
          >
          </b-form-select>
        </b-form-group>
      </b-col>

      <b-col lg="3">
        <b-form-group
          label="Show:"
          label-for="group-by"
          label-cols-sm="2"
          label-align-sm="left"
          label-size="sm"
          class="mb-0"
        >
          <b-form-select
            id="group-by"
            v-model="show"
            size="sm"
            :options="showOptions"
          >
          </b-form-select>
        </b-form-group>
      </b-col>

      <b-col lg="6" class="ml-auto">
        <b-input-group size="sm">
          <b-form-input
            id="filter-input"
            v-model="search"
            type="search"
            placeholder="Type to Search"
          ></b-form-input>
        </b-input-group>
      </b-col>
    </b-row>

    <b-table
      ref="statisticTable"
      striped
      sticky-header
      :busy="isBusyLocal"
      :filter="search"
      :filter-included-fields="searchOn"
      :items="localStatistics"
      :fields="parentFields"
      :head-variant="'dark'"
      :table-variant="'light'"
      responsive="sm"
      class="mt-2"
      :tbody-tr-class="rowClass"
      show-empty
    >
      <template #empty="scope">
        <div class="empty-row">
          <h4>{{ scope.emptyText }}</h4>
        </div>
      </template>
      <template #emptyfiltered="scope">
        <div class="empty-row">
          <h4>{{ scope.emptyFilteredText }}</h4>
        </div>
      </template>

      <template #table-busy>
        <div class="text-center data-loader my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>
      </template>

      <template #head(showDetails)="data">
        <div class="pointer" @click="expandCollapse">
          <b-icon-plus-square v-if="isAllCollapsed"> </b-icon-plus-square>

          <b-icon-dash-square v-if="!isAllCollapsed"> </b-icon-dash-square>
        </div>
      </template>

      <template #cell(completeness)="data">
        <div class="text-right" v-if="!data.item.allIgnored">
          {{ data.item.completeness }}%
        </div>
        <div class="text-right" v-if="data.item.allIgnored">All Excluded</div>
      </template>

      <template #cell(showDetails)="row">
        <b-icon-plus-square
          v-if="!row.detailsShowing"
          @click="row.toggleDetails"
          class="pointer"
        >
        </b-icon-plus-square>

        <b-icon-dash-square
          v-if="row.detailsShowing"
          @click="row.toggleDetails"
          class="pointer"
        >
        </b-icon-dash-square>
      </template>

      <template #row-details="row">
        <b-table
          sticky-header
          :head-variant="'dark'"
          :table-variant="'light'"
          :items="row.item.details"
          :fields="detailsFields"
          :tbody-tr-class="rowClassDetails"
        >
          <template #cell(completeness)="data">
            <div class="text-right" v-if="!data.item.ignoreTool">
              {{ data.item.completeness }}%
            </div>
            <div class="text-right" v-if="data.item.ignoreTool">Excluded</div>
          </template>
        </b-table>
      </template>
    </b-table>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import {
  CompletenessStatistic,
  CompletenessStatisticDetails
} from "@/entities/CompletenessStatistic";

@Component
export default class CompletenessReport extends Vue {
  @Prop()
  statistics!: CompletenessStatistic[];
  localStatistics: CompletenessStatistic[] = this.statistics;

  @Watch("statistics")
  onStatisticsChanged() {
    this.localStatistics = this.statistics;
  }

  @Prop()
  readonly groupBy!: string;
  groupByLocal: string = this.groupBy || "Branch";

  @Watch("groupByLocal")
  onLocalGroupByChanged() {
    this.$emit("update:groupBy", this.groupByLocal);
  }

  @Watch("groupBy")
  onGroupByChanged() {
    this.groupByLocal = this.groupBy;
  }

  @Prop({ default: false, type: Boolean })
  readonly isBusy!: boolean;
  isBusyLocal: boolean = this.isBusy || false;

  @Watch("isBusyLocal")
  onLocalIsBusyChanged() {
    this.$emit("update:isBusy", this.isBusyLocal);
  }

  @Watch("isBusy")
  onIsBusyChanged() {
    this.isBusyLocal = this.isBusy;
  }

  @Prop()
  groupByOptions!: string[];

  isAllCollapsed: boolean = true;
  search: string = "";
  searchOn = ["name"];

  show: string = "All";
  showOptions: string[] = ["All", "Only Completed", "Only Uncompleted"];

  @Watch("show")
  onShowChanged() {
    if (this.show === "All") {
      this.localStatistics = this.statistics;
    } else if (this.show === "Only Completed") {
      this.localStatistics = this.statistics.filter(
        s => s.completeness === 100
      );
    } else {
      this.localStatistics = this.statistics.filter(
        s => s.completeness !== 100
      );
    }
  }

  rowClassDetails(item: CompletenessStatisticDetails, type: string) {
    if (!item || type !== "row") return "";
    if (item.ignoreTool) return "table-warning";
  }

  rowClass(item: CompletenessStatistic, type: string) {
    if (!item || type !== "row") return "";
    if (item.allIgnored) return "table-warning";
  }

  get parentFields() {
    return [
      {
        key: "showDetails",
        label: "",
        sortable: false
      },
      {
        key: "name",
        label: this.groupByLocal,
        sortable: true
      },
      {
        key: "completeness",
        label: "Completeness",
        sortable: true
      }
    ];
  }

  get detailsFields() {
    let nameLabel = this.groupByLocal === "Branch" ? "Expert" : "Branch";
    let descriptionLabel =
      this.groupByLocal === "Treatment" ? "Expert" : "Treatment";

    return [
      {
        key: "firstFieldName",
        label: nameLabel,
        sortable: true
      },
      {
        key: "secondFieldName",
        label: descriptionLabel,
        sortable: true
      },
      {
        key: "completeness",
        label: "Completeness",
        sortable: true
      }
    ];
  }

  expandCollapse() {
    this.isBusyLocal = true;

    this.statistics.forEach(item =>
      this.$set(item, "_showDetails", this.isAllCollapsed)
    );
    this.isAllCollapsed = !this.isAllCollapsed;

    setTimeout(() => (this.isBusyLocal = false), 200);
  }
}
</script>

<style scoped>
.b-table-sticky-header {
  max-height: calc(100vh - 15rem);
}

.empty-row {
  text-align: center !important;
  padding: 1em;
}

.data-loader {
  color: #2F45A5;
}
</style>
